import { lt, showSuccessAlert } from '@mews/framework';
import { SuspenseBoundary, useDeleteConfirmationDialog } from '@mews-commander/ui';
import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { DataTestId } from '@mews-commander/enums';
import { useCentralCancellationPolicyDelete } from '../mutations';
import { WithCancellationPolicyId } from '../../../cancellationPolicies';

interface Params extends WithCancellationPolicyId {
    onSuccess?: () => void;
}

const DeleteConfirmationDialogContent = ({ cancellationPolicyId }: WithCancellationPolicyId) => {
    const {
        data: { centralCancellationPolicy },
    } = useRepositorySuspenseQuery('portfolioCentralCancellationPolicies', 'get', {
        centralCancellationPolicyId: cancellationPolicyId,
    });

    const isAssignedToRateGroups =
        centralCancellationPolicy?.propertyRateGroupCounts?.some(({ rateGroupCount }) => rateGroupCount > 0) ||
        centralCancellationPolicy?.propertyGroupRateGroupCounts?.some(({ rateGroupCount }) => rateGroupCount > 0);

    return isAssignedToRateGroups
        ? lt('CancellationPolicyDeleteDialogContent')
        : lt('PaymentPolicyDeleteDialogZeroEntitiesContent');
};

export const useDeleteCentralCancellationPolicyDialog = ({ cancellationPolicyId, onSuccess }: Params) => {
    const { mutateAsync } = useCentralCancellationPolicyDelete();

    const deleteAction = async () =>
        mutateAsync(
            { centralCancellationPolicyIds: [cancellationPolicyId] },
            {
                onSuccess: () => {
                    onSuccess?.();
                    showSuccessAlert({
                        title: lt('CancellationPolicyDeleteSuccessMessage'),
                    });
                },
            }
        );

    return useDeleteConfirmationDialog({
        confirmButtonDataTestId: DataTestId.ConfirmDeletePolicyButton,
        deleteAction,
        renderContent: () => (
            <SuspenseBoundary>
                <DeleteConfirmationDialogContent cancellationPolicyId={cancellationPolicyId} />
            </SuspenseBoundary>
        ),
        title: lt('CancellationPolicyDeleteDialogTitle'),
    });
};
